import React from "react"
import * as ReactTable from "react-table"
import Form from "react-bootstrap/Form"

import { GetApi } from "../../../../../api/api"
import { FeatureType, FeatureTypeGroup, AttributeTypeGroupUsage, Project } from "../../../../../api/types"
// import { Feedback } from '../../../../../../components/Validator';
import AppContext from "../../../../../components/AppContext"
import withModal, { WithModalProps, InjectedProps } from "../../../../../components/HOC/WithModal"
import Table from "../../../../../components/Table"
import CheckBox, { CheckBoxProps } from "../../../../../components/CheckBox"
import Pending from "../../../../../components/Pending"
import Button from "../../../../../components/Button"
import { AttributeTypeGroup } from "../../../../../api/generated/QuadriConfigurationApi"
import AssociateWithPropertySets from "./AssociateWithPropertySets"

interface FeatureTypeFlattened {
  id: number
  name: string
  groupId: number
  groupName: string
}

interface InnerEditProps {
  fccId: string
  atgId: number
  associations: AttributeTypeGroupUsage
  featureTypes: FeatureTypeFlattened[]
  featureTypeGroups: { [key: number]: string }
  featureTypesInGroup: { [key: number]: FeatureTypeFlattened[] }
  propertySets: AttributeTypeGroup[]
  disabled: boolean
  currentVersion: number
  pastVersion: { [key: number]: number }
  featureCatalog: Project
}

interface CheckBoxColumnProps {
  featureTypeCheckProps: CheckBoxProps
  featureTypeGroupCheckProps: CheckBoxProps
  useGroupCheckBox?: boolean
}

interface DataType {
  group: string
  featureType: {
    id: number
    name: string
  }
  checkBoxColumnProps: CheckBoxColumnProps
}

type ColumnType = ReactTable.Column<DataType>

type TableInstanceType = ReactTable.Instance<DataType>

interface InnerEditState {
  selectedFeatureTypes: { [key: number]: boolean }
  selectAll?: boolean
  search: string
  pivotBy: keyof DataType
  filterSelected: "ShowSelectedOnly" | "ShowUnselectedOnly" | "ShowAll"
  showPropertySetTypes: boolean
}

class InnerEdit extends React.Component<InnerEditProps, InnerEditState> {
  public static contextType = AppContext
  private readonly columns: ColumnType[]
  private readonly reactTable = React.createRef<TableInstanceType>()

  constructor(props: InnerEditProps) {
    super(props)

    // Convert from "AttributeTypeUsage" to an array of ids of selected types/groups
    const selectedFeatureTypesArray = (props.associations.featureTypeReferences || []).map((ftr) => ftr.featureTypeId)

    // Convert the arrays of ids to sets of selected Ids (each array entry gets a set entry "[id]: true")
    const getSelectedSet = (idArray: number[]) => idArray.reduce((p, n) => ({ ...p, [n]: true }), {})

    this.state = {
      selectedFeatureTypes: getSelectedSet(selectedFeatureTypesArray),
      search: "",
      pivotBy: "group",
      filterSelected: "ShowAll",
      showPropertySetTypes: false
    }

    this.columns = this.createColumns()
  }

  public render() {
    const data = this.getRowData()
    // const back = () => history.back();

    return (
      <>
        {this.state.showPropertySetTypes ? (
          <>
            <Form.Label>Copy Feature Type associations from Property Set </Form.Label>
            <AssociateWithPropertySets
              featureCatalog={this.props.featureCatalog}
              propertySets={this.props.propertySets}
              associations={this.props.associations}
              atgId={this.props.atgId}
              fccId={this.props.fccId}
              selectedFeatureTypesFromProp={this.state.selectedFeatureTypes}
            />
          </>
        ) : (
          <Form>
            {Object.keys(this.props.pastVersion).length > 0 && (
              <div className="message message-primary">
                <i className="modus-icons">info</i>A select number checkboxes can not be unchecked because the feature
                type association has already been introduced in a previous feature catalog version.
              </div>
            )}
            {this.filterActive() && (
              <div className="message message-warning">
                <i className="modus-icons">warning</i>
                Some feature types hidden by the filter.
              </div>
            )}
            <Form.Group controlId="searchInput">
              <Form.Label>Filter</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search Feature Type or Group"
                value={this.state.search}
                onChange={this.onSearchChange}
              />
              <CheckBox
                id="showSelectedOnlyFilter"
                label="Show selected"
                checked={this.state.filterSelected === "ShowSelectedOnly"}
                onToggle={this.toggleShowOnlyChecked}
              />
              <CheckBox
                id="showUnselectedOnlyFilter"
                label="Show unselected"
                checked={this.state.filterSelected === "ShowUnselectedOnly"}
                onToggle={this.toggleShowOnlyUnchecked}
              />
            </Form.Group>
            {/* <Form.Group>
          <div><label>Group by</label></div>
          <ButtonGroup>
            <Button disabled={true}>Feature Type Group</Button>
            <Button disabled={true}>Checked</Button>
          </ButtonGroup>
        </Form.Group> */}
            <Form.Group>
              <label className="d-block">Selection (uses filter)</label>
              <Button onClick={this.selectAllFiltered} className="mr-2">
                Select all
              </Button>
              <Button onClick={this.deselectAllFiltered} className="mr-2">
                Deselect all
              </Button>
              {/* <Button>Invert selection</Button> */}

              <Button onClick={this.showPropertySetTypes}>Import(Feature Selection)</Button>
              {}
            </Form.Group>

            <Table
              tableRef={this.reactTable}
              // filterable={true}
              data={data}
              columns={this.columns}
              pivotBy={[this.state.pivotBy]} // this creates the tree structure
              collapseOnDataChange={false}
              collapseOnSortingChange={false}
              collapseOnPageChange={false}
              // expanded={this.expandEverything(data)}
              minRows={0}
              // showPagination={true}
              // pageSize={10}
              defaultSorted={[
                {
                  id: "group",
                  desc: false
                }
              ]}
            />

            {/* <Button color='link' type="button" onClick={back}>
          <i className="icon-font tc-icon-arrow-line-back" />
          Back
        </Button> */}
          </Form>
        )}
      </>
    )
  }

  private showPropertySetTypes = () => {
    this.setState(() => ({
      showPropertySetTypes: !this.state.showPropertySetTypes
    }))
  }

  private toggleShowOnlyChecked = () => {
    this.setState((prevState) => ({
      filterSelected: prevState.filterSelected === "ShowSelectedOnly" ? "ShowAll" : "ShowSelectedOnly"
    }))
  }

  private toggleShowOnlyUnchecked = () => {
    this.setState((prevState) => ({
      filterSelected: prevState.filterSelected === "ShowUnselectedOnly" ? "ShowAll" : "ShowUnselectedOnly"
    }))
  }

  private getFilteredFeatureTypes = () => {
    let out = this.props.featureTypes
    if (this.state.search) {
      const search = this.state.search
      out = out.filter(
        (ft) =>
          ft.groupName.toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
          ft.name.toLowerCase().indexOf(search.toLowerCase()) !== -1
      )
    }

    switch (this.state.filterSelected) {
      case "ShowSelectedOnly":
        out = out.filter((ft) => ft.id in this.state.selectedFeatureTypes)
        break
      case "ShowUnselectedOnly":
        out = out.filter((ft) => !(ft.id in this.state.selectedFeatureTypes))
        break
      default:
        break
    }

    return out
  }

  private filterActive() {
    return this.state.filterSelected !== "ShowAll" || this.state.search
  }

  private getRowData(): DataType[] {
    const featureTypes = this.filterActive() ? this.getFilteredFeatureTypes() : this.props.featureTypes
    return featureTypes.map((featureType) => ({
      group: featureType.groupName,
      featureType: {
        id: featureType.id,
        name: featureType.name
      },
      checkBoxColumnProps: this.getCheckBoxColumnProps(featureType)
    }))
  }

  // private expandEverything(data: any[]) { return data.map(el => ({index: true})); }

  private selectedInPastVersion(featureType?: FeatureTypeFlattened) {
    return featureType ? featureType.id in this.props.pastVersion : false
  }

  private getCheckBoxColumnProps(featureType: FeatureTypeFlattened): CheckBoxColumnProps {
    return {
      featureTypeCheckProps: {
        disabled: this.selectedInPastVersion(featureType),
        checked: this.isFeatureTypeSelected(featureType),
        onToggle: (e: any) =>
          this.isFeatureTypeSelected(featureType)
            ? this.onUncheckFeatureType(featureType)
            : this.onCheckFeatureType(featureType)
      },
      featureTypeGroupCheckProps: {
        checked: this.isFeatureTypeGroupSelected(featureType.groupId),
        onToggle: (e: any) =>
          this.isFeatureTypeGroupSelected(featureType.groupId)
            ? this.onUncheckFeatureTypeGroup(featureType.groupId)
            : this.onCheckFeatureTypeGroup(featureType.groupId)
      }
    }
  }

  private isFeatureTypeSelected(featureType: FeatureTypeFlattened) {
    return featureType.id in this.state.selectedFeatureTypes // ||
  }

  private isFeatureTypeGroupSelected(featureTypeGroupId: number): boolean {
    const featureTypes = this.getFeatureTypesInGroupSet(featureTypeGroupId)
    const allInGroupSelected = Object.keys(featureTypes).reduce(
      (p, n) => p && n in this.state.selectedFeatureTypes,
      true
    )
    return allInGroupSelected
  }

  private onCheckFeatureType(featureType: FeatureTypeFlattened) {
    this.trySelectFeatureTypes([featureType])
  }

  private onUncheckFeatureType(featureType: FeatureTypeFlattened) {
    this.tryDeselectFeatureTypes([featureType])
  }

  private trySelectFeatureTypes(featureTypes: FeatureTypeFlattened[]) {
    return this.selectFeatureTypes(featureTypes)
  }

  // private tryDeselectFeatureTypes(featureTypes: FeatureTypeFlattened[] | "All") {
  //   if (featureTypes === "All") {
  //     this.deselectFeatureTypes(featureTypes);
  //   } else {
  //     this.deselectFeatureTypes(featureTypes
  //       .filter(ft => !this.selectedInPastVersion(ft))
  //     );
  //   }
  // }

  private selectFeatureTypes(featureTypes: FeatureTypeFlattened[]) {
    const selected = featureTypes.reduce((p, c) => ({ ...p, [c.id]: true }), {})
    this.setState(
      (prevState) => ({
        selectedFeatureTypes: {
          ...prevState.selectedFeatureTypes,
          ...selected
        }
      }),
      () => this.onApplyAssociation()
    )
  }

  private tryDeselectFeatureTypes(featureTypes: FeatureTypeFlattened[] | "All") {
    this.setState(
      (prevState) => {
        const selected = prevState.selectedFeatureTypes

        if (featureTypes !== "All") {
          const selectedFeatureTypes = { ...prevState.selectedFeatureTypes }
          for (const ft of featureTypes) {
            delete selectedFeatureTypes[ft.id]
          }
          return {
            selectedFeatureTypes: {
              ...selectedFeatureTypes,
              ...this.getLockedFeatureTypesSet(selected)
            }
          }
        } else {
          return { selectedFeatureTypes: this.getLockedFeatureTypesSet(selected) }
        }
      },
      () => this.onApplyAssociation()
    )
  }

  private getLockedFeatureTypesSet(selected: { [key: number]: boolean }) {
    const out = Object.keys(selected)
      .map((k) => parseInt(k, undefined))
      .filter((ftId) => {
        const featureType = this.props.featureTypes.find((ft) => ft.id === ftId)
        return this.selectedInPastVersion(featureType)
      })
      .reduce((p, n) => ({ ...p, [n]: true }), {})
    return out
  }

  // TODO: Add filter
  private onCheckFeatureTypeGroup(featureTypeGroupId: number) {
    this.trySelectFeatureTypes(this.getFeatureTypesInGroupArray(featureTypeGroupId))
  }

  private onUncheckFeatureTypeGroup(featureTypeGroupId: number) {
    this.tryDeselectFeatureTypes(this.props.featureTypes.filter((ft) => ft.groupId === featureTypeGroupId))
  }

  private onApplyAssociation = () =>
    GetApi()
      .then((api) =>
        api.updateAttributeTypeGroupAssociation(this.props.fccId, this.props.atgId, {
          featureTypeIds: this.setToIdArray(this.state.selectedFeatureTypes)
        })
      )
      .catch((error) => this.context.useToasts.addToast(error, "danger"))

  private getFeatureTypesInGroupSet(groupId: number): { [key: number]: boolean } {
    const arr = this.getFeatureTypesInGroupArray(groupId)
    const out: { [key: number]: boolean } = {}
    for (const ft of arr) {
      out[ft.id] = true
    }
    return out
  }

  private getFeatureTypesInGroupArray(groupId: number): FeatureTypeFlattened[] {
    return this.props.featureTypesInGroup[groupId]
  }

  private onSearchChange = (e: any) => {
    const search = e.target.value

    if (search !== "") {
      this.setState({ search })
    } else {
      this.setState({ search: "" })
    }
  }

  private createColumns(): ColumnType[] {
    return [
      {
        Header: "Feature Type Group",
        accessor: "group",
        Cell: (row: any) => row.value || ""
      },
      {
        Header: "Feature Type",
        accessor: "featureType",
        aggregate: (vals: string[]) => "",
        Cell: (row: any) => (row.value ? row.value.name : "")
      },
      {
        Header: "",
        accessor: "checkBoxColumnProps",
        width: 70,
        sortable: false,
        aggregate: (vals: CheckBoxColumnProps[]) => {
          const groupCheckProps = vals[0].featureTypeGroupCheckProps

          // if some children are selected but not group, then indeterminate
          const someSelected = vals.reduce((p, c) => c.featureTypeCheckProps.checked || p, false)
          const indeterminate: boolean = someSelected && !groupCheckProps.checked

          const groupProps: CheckBoxColumnProps = {
            ...vals[0],
            featureTypeGroupCheckProps: {
              ...groupCheckProps,
              indeterminate
            },
            useGroupCheckBox: true
          }
          return groupProps
        },
        Cell: (row: any) => {
          const value = row.value as CheckBoxColumnProps
          if (value.useGroupCheckBox) {
            // Aggregate
            return (
              <div className="align-center">
                <CheckBox {...value.featureTypeGroupCheckProps} disabled={value.featureTypeGroupCheckProps.disabled} />
              </div>
            )
          } else {
            // value is check box props
            return (
              <div className="align-center">
                <CheckBox {...value.featureTypeCheckProps} disabled={value.featureTypeCheckProps.disabled} />
              </div>
            )
          }
        }
      }
    ]
  }

  private selectAllFiltered = (e: any) => {
    if (this.state.search) {
      this.trySelectFeatureTypes(this.getFilteredFeatureTypes())
    } else {
      this.trySelectFeatureTypes(this.props.featureTypes)
    }
  }

  private deselectAllFiltered = (e: any) => {
    if (this.state.search) {
      this.tryDeselectFeatureTypes(this.getFilteredFeatureTypes())
    } else {
      this.tryDeselectFeatureTypes("All")
    }
  }

  private setToIdArray = (set: { [key: number]: boolean }) => Object.keys(set).map(this.parseIds).filter(this.validId)

  private parseIds = (id: string) => {
    const numId = parseInt(id, 10)
    if (numId === null) {
      console.error("parseInt failed for id: ", id)
      return -1
    } else {
      return numId
    }
  }

  private validId = (id: number) => id !== -1
}

interface EditProps extends InjectedProps {
  fccId: string
  atgId: number
  currentVersion: number
}

const Edit = (props: EditProps) => {
  const convertFeatureTypes = (
    featureTypes: FeatureType[],
    featureTypeGroupsMap: { [key: number]: string }
  ): FeatureTypeFlattened[] =>
    featureTypes
      .filter((ft) => ft.groups && ft.groups.filter((g) => g.primary).length === 1)
      .map((ft) => {
        const primaryGroupId = ft.groups!.find((g) => g.primary)!.groupId
        return {
          id: ft.id,
          name: ft.name,
          groupId: primaryGroupId,
          groupName: featureTypeGroupsMap[primaryGroupId]
        }
      })

  const convertFeatureTypeGroups = (featureTypeGroups: FeatureTypeGroup[]) =>
    featureTypeGroups.reduce((p, n) => ({ ...p, [n.id]: n.name }), {})

  const convertFeatureTypesInGroup = (featureTypes: FeatureTypeFlattened[], featureTypeGroups: FeatureTypeGroup[]) => {
    const out: { [key: number]: FeatureTypeFlattened[] } = {}
    for (const ft of featureTypes) {
      if (out[ft.groupId]) {
        out[ft.groupId].push(ft)
      } else {
        out[ft.groupId] = [ft] as FeatureTypeFlattened[]
      }
    }
    return out
  }

  const renderSuccess = (innerProps: InnerEditProps) => <InnerEdit {...innerProps} />

  const getData = async (): Promise<InnerEditProps> => {
    try {
      const api = await GetApi()
      const featureCatalog = await api.getFeatureCatalogCustomization(props.fccId)

      const fcId = featureCatalog.baseFeatureCatalogs[0].id // assume there is a base FC

      const [featureTypes, customFeatureTypes, featureTypeGroups, associations, propertySets] = await Promise.all([
        api.getFeatureTypes(fcId),
        api.getCustomFeatureTypes(props.fccId),
        api.getFeatureTypeGroups(fcId),
        api.getAttributeTypeGroupAssociations(props.fccId, props.atgId),
        api.getAttributeTypeGroups(props.fccId)
      ])
      const combinedFeatureTypes = [...featureTypes, ...customFeatureTypes]
      const featureTypeGroupsMap = convertFeatureTypeGroups(featureTypeGroups)
      const featureTypesFlattened = convertFeatureTypes(combinedFeatureTypes, featureTypeGroupsMap)
      const ftRefs = associations.featureTypeReferences || []

      if (combinedFeatureTypes && combinedFeatureTypes.length > 0) {
        return {
          ...props,
          featureCatalog,
          associations,
          featureTypes: featureTypesFlattened,
          featureTypeGroups: featureTypeGroupsMap,
          featureTypesInGroup: convertFeatureTypesInGroup(featureTypesFlattened, featureTypeGroups),
          propertySets,
          disabled: props.disabled || !props.valid.valid,
          pastVersion: ftRefs
            .filter((ftr) => ftr.introducedInVersion !== props.currentVersion)
            .reduce((p, n) => ({ ...p, [n.featureTypeId]: n.introducedInVersion }), {})
        }
      } else {
        throw new Error("The selected base feature catalog has no types.")
      }
    } catch (error) {
      throw new Error(error as any)
    }
  }

  return <Pending getData={getData} renderSuccess={renderSuccess} />
}

export interface EditModalProps extends WithModalProps {
  fccId: string
  atgId: number
  currentVersion: number
}

const EditModal = withModal(Edit)

export default EditModal

// Some nice tests to have with this component:
//
// State:
//   If you select an unselected group: The group should be selected, the sub-features should be
//   unselected.
//
// - Filtering works
// - Select all works as expected + unselect
// - If the association is from previous version. Lock it...
